import dayjs, { Dayjs } from "dayjs"
import {
  SEASONAL_ADJUSTMENT_WINTER,
  SEASONAL_ADJUSTMENT_SUMMER,
  MINIMUM_CREDIT_TO_RENEW_WITH_CREDIT_IN_POUNDS,
} from "@/constants"
import { safeDate } from "@/helpers/dateHelper"
import {
  ElecProductType,
  ElecRate,
  RateType,
  RenewalQuoteRaw,
  TariffType,
  FuelType,
} from "@/types/RenewalQuote.types"
import { safeNumber } from "@/helpers/numberHelpers"

export class RenewalQuote {
  quoteId: number
  shortTariffName: string
  tariffName: string
  rate: RateType
  fuelType: FuelType
  isRecommended: boolean
  costPerYearInPounds: number
  costPerMonthInPounds: number
  tariffLengthInMonths: number
  elecProductType: ElecProductType
  tariffType: TariffType
  elecRates?: ElecRate[]
  elecStandingChargeInPence?: number
  elecConsumptionPerYearInKwh?: string
  gasCostPerKwhInPence?: number
  gasStandingChargeInPence?: number
  gasConsumptionPerYearInKwh?: string
  earlyExitFeePerFuelInPounds?: number
  elecProductCode?: string
  gasProductCode?: string
  renewalElecStartDate?: Dayjs
  renewalGasStartDate?: Dayjs
  mustSetUpHHReadingConsent: boolean
  elecMeterPointIdentifier?: string
  gasMeterPointIdentifier?: string
  accountBalance: number
  elecAnnualCostInPounds?: number
  gasAnnualCostInPounds?: number

  constructor(rawQuote: RenewalQuoteRaw, accountBalance: number) {
    this.quoteId = rawQuote.quoteId
    this.tariffName = rawQuote.tariffName
    this.rate = rawQuote.rate
    this.fuelType = rawQuote.fuelType
    this.isRecommended = !!rawQuote.isRecommendedForRenewal
    this.costPerYearInPounds = rawQuote.costPerYearInPounds
    this.costPerMonthInPounds = rawQuote.costPerMonthInPounds
    this.shortTariffName = rawQuote.shortTariffName
    this.tariffLengthInMonths = rawQuote.tariffLengthInMonths ?? 0
    this.tariffType = rawQuote.tariffType ?? TariffType.Standard
    this.earlyExitFeePerFuelInPounds = safeNumber(
      rawQuote.earlyExitFeePerFuelInPounds
    )
    this.accountBalance = accountBalance
    this.elecAnnualCostInPounds = rawQuote.elecAnnualCostInPounds
    this.gasAnnualCostInPounds = rawQuote.gasAnnualCostInPounds
    // Electricity specific
    this.elecProductCode = rawQuote.elecProductCode
    this.elecMeterPointIdentifier = rawQuote.elecMeterPointIdentifier
    this.elecConsumptionPerYearInKwh = rawQuote.elecConsumptionPerYearInKwh
    this.elecProductType = rawQuote.elecProductType ?? ElecProductType.Standard
    this.renewalElecStartDate = safeDate(rawQuote.renewalElecStartDate)
    this.mustSetUpHHReadingConsent = !!rawQuote.mustSetUpHHReadingConsent
    this.elecStandingChargeInPence = safeNumber(
      rawQuote.elecStandingChargeInPence
    )
    this.elecRates = rawQuote.elecRates?.map((rate) => ({
      costPerKwhInPence: Number(rate.costPerKwhInPence),
      name: rate.name,
      fromTime: rate.fromTime,
      toTime: rate.toTime,
      eco7ElecConsumptionPerYearInKwh: rate.eco7ElecConsumptionPerYearInKwh,
    }))

    // Gas specific
    this.gasProductCode = rawQuote.gasProductCode
    this.gasMeterPointIdentifier = rawQuote.gasMeterPointIdentifier
    this.gasConsumptionPerYearInKwh = rawQuote.gasConsumptionPerYearInKwh
    this.gasCostPerKwhInPence = safeNumber(rawQuote.gasCostPerKwhInPence)
    this.renewalGasStartDate = safeDate(rawQuote.renewalGasStartDate)
    this.gasStandingChargeInPence = safeNumber(
      rawQuote.gasStandingChargeInPence
    )
  }

  get tariffCode(): string {
    const productCode = this.elecProductCode ?? this.gasProductCode
    const code = productCode?.split("-")[1] ?? "unknown"
    return `${code}-${this.tariffLengthInMonths}`
  }

  get isVariableRate(): boolean {
    // To be reverted back after BE is updated https://soenergy.atlassian.net/browse/SO-24553
    // return this.rate === RateType.Variable

    return (
      this.tariffCode.startsWith("SGRN") || this.tariffCode.startsWith("SOOC")
    )
  }

  get isFixedRate(): boolean {
    return !this.isVariableRate
  }

  get isVariableRateGreen(): boolean {
    return this.tariffType === TariffType.Green && this.isVariableRate
  }

  get isEvTariff(): boolean {
    return this.tariffType === TariffType.EV
  }

  get hasDualFuel(): boolean {
    return this.fuelType === FuelType.DualFuel
  }

  get hasGas(): boolean {
    return this.hasDualFuel || this.fuelType === FuelType.Gas
  }

  get hasElec(): boolean {
    return this.hasDualFuel || this.fuelType === FuelType.Electricity
  }

  get annualStandingChargeInPounds(): number {
    let standingChargePerDayInPence = 0

    if (this.hasElec && this.elecStandingChargeInPence) {
      standingChargePerDayInPence += this.elecStandingChargeInPence
    }
    if (this.hasGas && this.gasStandingChargeInPence) {
      standingChargePerDayInPence += this.gasStandingChargeInPence
    }

    return (standingChargePerDayInPence * 365) / 100
  }

  get fuelTypeFormatted(): string {
    if (this.hasDualFuel) return "Dual Fuel"
    else if (this.hasElec) return "Electricity only"
    return "Gas only"
  }

  get expectedElecEndDate(): Dayjs | null {
    if (!this.tariffLengthInMonths || !this.renewalElecStartDate) return null
    return this.renewalElecStartDate
      .add(this.tariffLengthInMonths, "months")
      .subtract(1, "day")
  }

  get expectedGasEndDate(): Dayjs | null {
    if (!this.tariffLengthInMonths || !this.renewalGasStartDate) return null
    return this.renewalGasStartDate
      .add(this.tariffLengthInMonths, "months")
      .subtract(1, "day")
  }

  get isSameStartDate(): boolean {
    if (!this.renewalElecStartDate || !this.renewalGasStartDate) return false
    return (
      this.renewalElecStartDate.diff(this.renewalGasStartDate, "days") === 0
    )
  }

  get tariffDurationFormatted(): string {
    if (this.isVariableRate) return "Variable Tariff"

    if (this.tariffLengthInMonths % 12 === 0) {
      const durationInYears = ["One", "Two", "Three", "Four", "Five", "Six"][
        Math.trunc(this.tariffLengthInMonths / 12) - 1
      ]
      return `${durationInYears} Year Tariff`
    }
    return `${this.tariffLengthInMonths} Month Tariff`
  }

  get isMissingConsumption(): boolean {
    if (
      this.hasElec &&
      (!this.elecConsumptionPerYearInKwh ||
        this.elecConsumptionPerYearInKwh === "0 kWh")
    ) {
      return true
    }

    if (
      this.hasGas &&
      (!this.gasConsumptionPerYearInKwh ||
        this.gasConsumptionPerYearInKwh === "0 kWh")
    ) {
      return true
    }

    return false
  }

  get tariffNameFormatted(): string {
    // multi-tariff quote
    if (this.tariffName.includes("/")) {
      return this.tariffName
    }
    if (this.tariffType === TariffType.Green) {
      return `${this.shortTariffName} Green`
    }
    if (this.tariffType === TariffType.EV) {
      return `${this.shortTariffName} EV`
    }

    return this.shortTariffName
  }

  canUseExcessCredit(isSeasonal: boolean): boolean {
    return (
      this.excessCredit(isSeasonal) >
      MINIMUM_CREDIT_TO_RENEW_WITH_CREDIT_IN_POUNDS
    )
  }

  excessCredit(isSeasonal: boolean): number {
    const now = dayjs()
    // If winter month then divide annual cost by 6, otherwise divide by 12
    const isWinterMonth = now.month() <= 1 || now.month() >= 8
    const costDivisor = isWinterMonth && !isSeasonal ? 6 : 12
    const positiveAccountBalance = Math.max(this.accountBalance, 0)
    const excessCredit =
      positiveAccountBalance - this.costPerYearInPounds / costDivisor

    return excessCredit > 0 ? excessCredit : 0
  }

  annualCostAdjusted(includeCredit: boolean, isSeasonal: boolean): number {
    const annualCostAdjusted =
      this.costPerYearInPounds -
      (includeCredit ? this.excessCredit(isSeasonal) : 0)

    return Math.max(annualCostAdjusted, this.annualStandingChargeInPounds)
  }

  monthlyCost(includeCredit: boolean, isSeasonal: boolean): number {
    return this.annualCostAdjusted(includeCredit, isSeasonal) / 12
  }

  getAdjustedMonthlyCost(
    adjustmentFactor: number,
    includeCredit: boolean
  ): number {
    return this.monthlyCost(includeCredit, true) * adjustmentFactor
  }

  monthlyCostSummer(includeCredit: boolean): number {
    return this.getAdjustedMonthlyCost(
      SEASONAL_ADJUSTMENT_SUMMER,
      includeCredit
    )
  }

  monthlyCostWinter(includeCredit: boolean): number {
    return this.getAdjustedMonthlyCost(
      SEASONAL_ADJUSTMENT_WINTER,
      includeCredit
    )
  }

  checkStandingChargeLargerThanAnnualCost(isSeasonal: boolean) {
    return (
      this.annualStandingChargeInPounds >
      this.annualCostAdjusted(true, isSeasonal)
    )
  }
}
